'use client';

/**
 * CoinBalance Component
 *
 * Displays the user's current gold coin balance with React Query caching.
 * Balance is cached client-side and only refetched after spending/purchasing coins.
 * Clicking opens the buy coins modal — or routes to /pricing (premium) while
 * coin purchases are hidden (COIN_PURCHASES_ENABLED=false).
 */
import { useState } from 'react';

import { useRouter } from 'next/navigation';

import { Loader2, Plus } from 'lucide-react';

import { Trans } from '@kit/ui/trans';

import { COIN_PURCHASES_ENABLED } from '~/config/monetization.config';

import { BuyCoinsModal } from './buy-coins-modal';
import { RoseIcon } from './rose-icon';
import { useCoinBalance, useInvalidateCoinBalance } from './use-coin-balance';

interface CoinBalanceProps {
  /**
   * Display variant
   * - 'compact': Just the number and icon
   * - 'full': Shows "Gold Coins" label
   */
  variant?: 'compact' | 'full';

  /**
   * Show the buy button
   */
  showBuyButton?: boolean;

  /**
   * Custom class name
   */
  className?: string;
}

export function CoinBalance({
  variant = 'compact',
  showBuyButton = true,
  className = '',
}: CoinBalanceProps) {
  const { data, isLoading } = useCoinBalance();
  const invalidateBalance = useInvalidateCoinBalance();
  const [showBuyModal, setShowBuyModal] = useState(false);
  const router = useRouter();

  const handleBuyClick = () =>
    COIN_PURCHASES_ENABLED ? setShowBuyModal(true) : router.push('/pricing');

  const balance = data?.coins ?? 0;

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Rose balance pill: rose + amount, divider, pink plus. */}
      <div
        className={`flex flex-shrink-0 items-center rounded-full ${className}`}
        style={{
          height: showBuyButton ? 44 : 36,
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.03)',
        }}
      >
        <button
          onClick={handleBuyClick}
          className="flex h-full items-center gap-2 rounded-full pr-3 pl-3 transition-colors hover:bg-white/5 sm:pl-4"
          data-test="navbar-rose-balance"
        >
          <RoseIcon className={showBuyButton ? 'h-6 w-6' : 'h-5 w-5'} />
          <span
            className="font-semibold text-white tabular-nums"
            style={{ fontSize: showBuyButton ? 16 : 14 }}
          >
            {balance.toLocaleString()}
          </span>
          {variant === 'full' && (
            <span className="hidden text-xs text-white/60 sm:inline">
              <Trans i18nKey="common:gold" />
            </span>
          )}
        </button>

        {showBuyButton && (
          <>
            <span
              aria-hidden
              style={{
                width: 1,
                height: 22,
                background: 'rgba(255,255,255,0.16)',
              }}
            />
            <button
              onClick={handleBuyClick}
              aria-label="Get more roses"
              className="flex h-full items-center rounded-full pr-4 pl-3 text-pink-500 transition-colors hover:bg-white/5 hover:text-pink-300"
              data-test="navbar-rose-buy"
            >
              <Plus className="h-5 w-5" strokeWidth={2.25} />
            </button>
          </>
        )}
      </div>

      {/* Buy Coins Modal — not mounted while pack purchases are hidden */}
      {COIN_PURCHASES_ENABLED && (
        <BuyCoinsModal
          open={showBuyModal}
          onOpenChange={setShowBuyModal}
          onPurchaseComplete={invalidateBalance}
        />
      )}
    </>
  );
}

/**
 * Compact coin display for use in tight spaces
 */
export function CoinBalanceCompact({ className = '' }: { className?: string }) {
  return (
    <CoinBalance
      variant="compact"
      showBuyButton={false}
      className={className}
    />
  );
}

// Re-export hooks for external use
export {
  useCoinBalance,
  useInvalidateCoinBalance,
  useCoinCount,
} from './use-coin-balance';
